import { useMessageStore, useUserStore } from 'store';
import {
  getChatAdminUuidList,
  getUnreadMessagesCount,
  pushNotificationMessage,
  updateChannelActive,
  updateClientChannelActive,
  createManagementClientChannel,
} from 'api';
import React, { useCallback } from 'react';
import {
  REFETCH_UNREAD_COUNT_TIMEOUT,
  TEXT_STRING,
  queryKeys,
  routes,
} from '../constants';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  TBodyPushNotification,
  TResPaymentGift,
  TUnreadCount,
  TUser,
} from 'types';
import Pubnub from 'pubnub';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

type TUseMessage = {
  enabled?: boolean;
  pubnub?: Pubnub;
};

function useMessage(payload?: TUseMessage) {
  const { enabled = false, pubnub } = payload || {};

  // Hooks
  const {
    unreadCount,
    setUnreadCount,
    updateUnreadCount,
    resetUnreadCount,
    setAdminList,
  } = useMessageStore();
  const timeoutRef = React.useRef<NodeJS.Timeout>();
  const { user, isAuthenticated, clientId, mgmChannelId, setMgmChannelId } =
    useUserStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Queries
  const { data, refetch } = useQuery<{ channels: TUnreadCount }>({
    refetchOnMount: true,
    queryKey: [queryKeys.UNREAD_COUNT],
    queryFn: getUnreadMessagesCount,
    enabled: enabled,
  });

  const { data: adminList } = useQuery<{ data: string[] }>({
    queryKey: [queryKeys.ADMIN_LIST],
    queryFn: getChatAdminUuidList,
    enabled: enabled,
  });

  // Mutations
  const { mutate: mutatePushNotification } = useMutation({
    mutationFn: (body: TBodyPushNotification) => {
      return pushNotificationMessage(body);
    },
    retry: 0,
  });

  const { mutate: mutateChannelActive } = useMutation({
    mutationFn: ({ channelId }: { channelId: string }) => {
      return isAuthenticated
        ? updateChannelActive(channelId)
        : updateClientChannelActive(channelId, clientId || '');
    },
    retry: 0,
  });

  const { mutateAsync: createMgmChannel, isPending: isCreatingMgmChannel } =
    useMutation({
      mutationFn: async () => {
        return createManagementClientChannel(clientId || '', false);
      },
      onSuccess(res) {
        if (res?.id) {
          setMgmChannelId(res.id);
        }
      },
      onError(err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        toast.error(msg);
      },
      retry: 0,
    });

  const sendMessage = useCallback(
    async (payload: TResPaymentGift) => {
      if (!pubnub) return;
      const {
        message,
        image: giftImage,
        animations: giftAnimations,
        name: giftName,
        channel,
      } = payload;
      let messageBody = `${message.trim()}\n`;
      if (giftName)
        messageBody += TEXT_STRING.COMMON.QUOTE.replace('$value', giftName);
      const notificationTitle = `${user?.name || ''} ${
        TEXT_STRING.COMMON.RECEIVED_MESSAGE
      }`;
      const gift = {
        image: giftImage,
        animations: giftAnimations,
        name: giftName,
      };

      const messagePayload = {
        text: message,
        pn_gcm: {
          notification: {
            title: notificationTitle,
            body: messageBody,
            sound: 'default',
          },
          data: {
            key: channel?.id,
            type: channel?.type,
          },
        },
        gift,
      };

      if (message || gift) {
        await pubnub.publish({
          channel: channel?.id,
          message: messagePayload,
        });
      }

      mutatePushNotification({
        channelId: channel?.id,
        channelType: channel?.type || '',
        message: messageBody,
        title: notificationTitle,
      });

      // send channel active lastMessage
      mutateChannelActive({ channelId: channel?.id });

      const channelUsers = channel?.members?.filter(
        (item) => item.uuid !== user?.uuid,
      );

      if (channelUsers) {
        Promise.all(
          channelUsers.map(({ uuid }) =>
            pubnub.publish({
              channel: uuid,
              message: {
                channel: channel?.id,
                text: messageBody,
              },
            }),
          ),
        );
      }
    },
    [
      mutateChannelActive,
      mutatePushNotification,
      pubnub,
      user?.name,
      user?.uuid,
    ],
  );

  React.useEffect(() => {
    if (data?.channels) {
      setUnreadCount(data?.channels);
    }
  }, [data, setUnreadCount]);

  React.useEffect(() => {
    if (adminList?.data) {
      setAdminList(adminList?.data);
    }
  }, [adminList, setAdminList]);

  const refetchAPI = useCallback(() => {
    if (timeoutRef.current) return;
    timeoutRef.current = setTimeout(() => {
      refetch();
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }, REFETCH_UNREAD_COUNT_TIMEOUT);
  }, [refetch]);

  const findOrCreateMgmChannelAndNavigate = useCallback(async () => {
    if (isCreatingMgmChannel) return;

    let channelId: string | undefined;

    if (isAuthenticated) {
      const cachedUser = queryClient.getQueryData<TUser>([
        queryKeys.CURRENT_USER,
      ]);
      channelId = user?.managementChannelId ?? cachedUser?.managementChannelId;
    } else {
      channelId = mgmChannelId || (await createMgmChannel())?.id;
    }

    if (!channelId) {
      toast.error(TEXT_STRING.ERROR.MGM_CHANNEL_NOT_FOUND);
      return;
    }

    navigate(routes.CONVERSATION.replace(':id', channelId));

    return channelId;
  }, [
    isCreatingMgmChannel,
    isAuthenticated,
    user?.managementChannelId,
    mgmChannelId,
    createMgmChannel,
    navigate,
    queryClient,
  ]);

  return {
    unreadCount,
    setUnreadCount,
    updateUnreadCount,
    resetUnreadCount,
    refetch: refetchAPI,
    sendMessage,
    findOrCreateMgmChannelAndNavigate,
  };
}

export default useMessage;
